import {
  AnyButtonActionDeclaration,
  AnyControlDeclaration,
  AnySignalIdentifierDeclaration,
  AnySignalSourceDeclaration,
  ApplicationDeclaration,
  BarChartControlDeclaration,
  BooleanSignalControlDeclaration,
  ButtonControlDeclaration,
  EnumSignalControlDeclaration,
  formatVersion,
  GaugeControlDeclaration,
  GridLayoutCellControlDeclaration,
  GridLayoutControlDeclaration,
  GridLayoutRowControlDeclaration,
  HeaderControlDeclaration,
  HintControlDeclaration,
  ImageControlDeclaration,
  LabelControlDeclaration,
  LineChartControlDeclaration,
  NumericSignalControlDeclaration,
  PageDeclaration,
  PageVariationDeclaration,
  RootControlDeclaration,
  ScreenTypeDeclaration,
  SignalFormatDeclaration,
  SignalScaleDeclaration,
  StackLayoutControlDeclaration,
  StringSignalControlDeclaration,
  TextControlDeclaration,
} from './schema';
import { ClientDeviceSignalType } from '../../../host/dev/client-device';
import { calculateHoverColor, calculateTextColor } from '../../../utils/colors';
import { Application } from '../../application';
import { Control } from '../../control';
import { Area } from '../../controls/area';
import { Bar, BarChart } from '../../controls/bar-chart';
import { BooleanSignal } from '../../controls/boolean-signal';
import {
  Button,
  ButtonAction,
  NavigatePageButtonAction,
  SetPulseButtonAction,
  WriteSignalsButtonAction,
} from '../../controls/button';
import { EnumSignal } from '../../controls/enum-signal';
import { Gauge } from '../../controls/gauge';
import { GridLayout, GridLayoutCell, GridLayoutRow } from '../../controls/grid-layout';
import { Header, HeaderPreset } from '../../controls/header';
import { Hint } from '../../controls/hint';
import { Image } from '../../controls/image';
import { Label } from '../../controls/label';
import { Line, LineChart } from '../../controls/line-chart';
import { NumericSignal } from '../../controls/numeric-signal';
import { Root } from '../../controls/root';
import { StackLayout } from '../../controls/stack-layout';
import { StringSignal } from '../../controls/string-signal';
import { SignalBinding, Text } from '../../controls/text';
import { Page } from '../../page';
import { PageVariation } from '../../page-variation';
import { ScreenType } from '../../screen-type';
import {
  ArraySignalIdentifier,
  ClientDeviceSignalSource,
  EcuSignalSource,
  SignalFormatter,
  SignalIdentifier,
  SignalScale,
  SignalSource,
} from '../../signal-identifier';
import { AdapterContract } from '../contract';

export class ApplicationAdapter implements AdapterContract {
  createApplication(declaration: ApplicationDeclaration): Application {
    const screenTypes = (declaration.screenTypes ?? []).map((item) => this.createScreenType(item));
    const screenTypesMap = new Map<string, ScreenType>(screenTypes.map((item) => [item.id, item]));

    return new Application({
      pages: declaration.pages.map((item) => this.createPage(item, screenTypesMap)),
      themeOptions: {
        headerPresets: declaration.themeOptions.headerPresets.map((preset) => {
          return new HeaderPreset(preset);
        }),
        brandColor: {
          background: declaration.themeOptions.brandColor.background,
          hover: calculateHoverColor(declaration.themeOptions.brandColor.background),
          text: calculateTextColor(declaration.themeOptions.brandColor.background),
        },
        primaryColor: {
          ...declaration.themeOptions.primaryColor,
          hover: calculateHoverColor(declaration.themeOptions.primaryColor.background),
          text: calculateTextColor(declaration.themeOptions.primaryColor.background),
        },
        pageColor: {
          ...declaration.themeOptions.pageColor,
          hover: calculateHoverColor(declaration.themeOptions.pageColor.background),
          text: calculateTextColor(declaration.themeOptions.pageColor.background),
        },
      },
      screenTypes,
      formatVersion: formatVersion,
    });
  }

  createScreenType(declaration: ScreenTypeDeclaration): ScreenType {
    return new ScreenType(declaration);
  }

  createPage(declaration: PageDeclaration, screenTypesMap: Map<string, ScreenType>): Page {
    const pages = declaration.pages?.map((child) => this.createPage(child, screenTypesMap));
    const controls = declaration.controls?.map((item) => this.createControl(item));
    const variations = declaration.variations?.map((item) => this.createPageVariation(item, screenTypesMap));

    return new Page({
      id: declaration.id,
      name: declaration.name,
      pages,
      controls,
      variations,
    });
  }

  createPageVariation(declaration: PageVariationDeclaration, screenTypesMap: Map<string, ScreenType>): PageVariation {
    let screenType: ScreenType;
    if (declaration.screenTypeId != null) {
      screenType = screenTypesMap.get(declaration.screenTypeId);
      if (screenType == null) {
        throw new Error(`Page variation references unknown screen type: ${declaration.screenTypeId}.`);
      }
    } else {
      screenType = null;
    }

    return new PageVariation({
      screenType,
      controlRoot: this.createRootControl(declaration.controlRoot),
    });
  }

  createControl(data: AnyControlDeclaration): Control {
    switch (data.type) {
      case 'BarChart':
        return this.createBarChartControl(data);
      case 'BooleanSignal':
        return this.createBooleanSignalControl(data);
      case 'Button':
        return this.createButtonControl(data);
      case 'EnumSignal':
        return this.createEnumSignalControl(data);
      case 'Gauge':
        return this.createGaugeControl(data);
      case 'GridLayout':
        return this.createGridLayoutControl(data);
      case 'GridLayoutRow':
        return this.createGridLayoutRowControl(data);
      case 'GridLayoutCell':
        return this.createGridLayoutCellControl(data);
      case 'Hint':
        return this.createHintControl(data);
      case 'Image':
        return this.createImageControl(data);
      case 'Label':
        return this.createLabelControl(data);
      case 'LineChart':
        return this.createLineChartControl(data);
      case 'NumericSignal':
        return this.createNumericSignalControl(data);
      case 'StackLayout':
        return this.createStackLayoutControl(data);
      case 'Text':
        return this.createTextControl(data);
      case 'Header':
        return this.createHeaderControl(data);
      case 'StringSignal':
        return this.createStringSignalControl(data);
      default:
        throw new Error(`Unsupported control type: ${(data as any).type}.`);
    }
  }

  createSignalIdentifier(data: AnySignalIdentifierDeclaration): SignalIdentifier {
    switch (data.type) {
      case 'SignalIdentifier':
        return new SignalIdentifier({
          signalType: data.signalType,
          unit: data.unit,
          options: data.options,
          scale: data.scale != null ? this.createSignalScale(data.scale) : undefined,
          formatter: data.format != null ? this.createSignalFormat(data.format) : undefined,
          source: data.source != null ? this.createSignalSource(data.source) : undefined,
        });
      case 'ArraySignalIdentifier':
        return new ArraySignalIdentifier({
          signalType: data.signalType,
          index: data.index,
          unit: data.unit,
          options: data.options,
          scale: data.scale != null ? this.createSignalScale(data.scale) : undefined,
          formatter: data.format != null ? this.createSignalFormat(data.format) : undefined,
          source: data.source != null ? this.createSignalSource(data.source) : undefined,
        });
      default:
        throw new Error(`Unsupported signal identifier type: ${(data as any).type}.`);
    }
  }

  createSignalSource(data: AnySignalSourceDeclaration) {
    switch (data.type) {
      case 'EcuSignalSource':
        return new EcuSignalSource({
          signalName: data.signalName,
          ecuId: data.ecuId,
        });
      case 'ClientDeviceSignalSource':
        return new ClientDeviceSignalSource({
          signalName: data.signalName as ClientDeviceSignalType,
        });
      default:
        throw new Error(`Unsupported signal source type: ${(data as any).type}.`);
    }
  }

  createSignalScale(data: SignalScaleDeclaration) {
    return new SignalScale(data);
  }

  createSignalFormat(data: SignalFormatDeclaration) {
    return new SignalFormatter(data);
  }

  createLabelControl(declaration: LabelControlDeclaration): Label {
    return new Label(declaration);
  }

  createSignalBinding({ signalID, ...declaration }: TextControlDeclaration.SignalBinding): SignalBinding {
    return new SignalBinding({ ...declaration, signalId: this.createSignalIdentifier(signalID) });
  }

  createTextControl({ type: _, ...declaration }: TextControlDeclaration): Text {
    return new Text({
      ...declaration,
      signals: declaration.signals.map((signal) => this.createSignalBinding(signal)),
    });
  }

  createHintControl(declaration: HintControlDeclaration): Hint {
    return new Hint(declaration);
  }

  createLine({ signalID, ...declaration }: LineChartControlDeclaration.Line): Line {
    return new Line({ ...declaration, signalId: this.createSignalIdentifier(signalID) });
  }

  createLineChartControl({ type: _, ...declaration }: LineChartControlDeclaration): LineChart {
    return new LineChart({
      ...declaration,
      lines: declaration.lines.map((line) => this.createLine(line)),
    });
  }

  createArea(declaration: Area): Area {
    return new Area(declaration);
  }

  createGaugeControl({ type: _, signalID, ...declaration }: GaugeControlDeclaration): Gauge {
    return new Gauge({
      ...declaration,
      signalId: this.createSignalIdentifier(signalID),
      areas: declaration.areas.map((area) => this.createArea(area)),
    });
  }

  createNumericSignalControl({ signalID, type: _, ...rest }: NumericSignalControlDeclaration): NumericSignal {
    return new NumericSignal({
      signalId: this.createSignalIdentifier(signalID),
      ...rest,
      areas: rest.areas.map((area) => this.createArea(area)),
    });
  }

  createEnumSignalControl({ signalID, type: _, ...rest }: EnumSignalControlDeclaration): EnumSignal {
    return new EnumSignal({ signalId: this.createSignalIdentifier(signalID), ...rest });
  }

  createBooleanSignalControl({ signalID, type: _, ...rest }: BooleanSignalControlDeclaration): BooleanSignal {
    return new BooleanSignal({ signalId: this.createSignalIdentifier(signalID), ...rest });
  }

  createBar({ signalID, ...declaration }: BarChartControlDeclaration.Bar): Bar {
    return new Bar({ ...declaration, signalId: this.createSignalIdentifier(signalID) });
  }

  createBarChartControl({ type: _, ...declaration }: BarChartControlDeclaration): BarChart {
    return new BarChart({
      ...declaration,
      bars: declaration.bars.map((bar) => this.createBar(bar)),
    });
  }

  createButtonControl({ type: _, action, ...options }: ButtonControlDeclaration): Button {
    return new Button({ action: this.createButtonAction(action), ...options });
  }

  createButtonAction(declaration: AnyButtonActionDeclaration): ButtonAction {
    switch (declaration.type) {
      case 'navigatePage':
        return new NavigatePageButtonAction({ pageId: declaration.pageId });
      case 'writeSignals':
        return new WriteSignalsButtonAction();
      case 'setPulse':
        return new SetPulseButtonAction({ signalId: this.createSignalIdentifier(declaration.signalId) });
    }
  }

  createImageControl(declaration: ImageControlDeclaration): Image {
    return new Image(declaration);
  }

  createStackLayoutControl({ type: _, children, ...options }: StackLayoutControlDeclaration): StackLayout {
    return new StackLayout({
      ...options,
      children: children.map((item) => this.createControl(item)),
    });
  }

  createGridLayoutControl({ type: _, children, ...options }: GridLayoutControlDeclaration): GridLayout {
    return new GridLayout({
      ...options,
      children: children.map((item) => this.createControl(item) as GridLayoutRow),
    });
  }

  createGridLayoutRowControl({ type: _, children, ...options }: GridLayoutRowControlDeclaration): GridLayoutRow {
    return new GridLayoutRow({
      ...options,
      children: children.map((item) => this.createControl(item) as GridLayoutCell),
    });
  }

  createGridLayoutCellControl({ type: _, children, ...options }: GridLayoutCellControlDeclaration) {
    return new GridLayoutCell({
      ...options,
      children: children.map((item) => this.createControl(item)),
    });
  }

  createHeaderControl(declaration: HeaderControlDeclaration): Header {
    return new Header(declaration);
  }

  createRootControl({ id, children }: RootControlDeclaration): Root {
    return new Root({ id, children: children.map((item) => this.createControl(item)) });
  }

  createStringSignalControl({ signalID, type: _, ...rest }: StringSignalControlDeclaration): StringSignal {
    return new StringSignal({ signalId: this.createSignalIdentifier(signalID), ...rest });
  }

  serializeControl(control: Control): AnyControlDeclaration {
    let json;

    if (control instanceof BarChart) {
      json = this.serializeBarChart(control);
    } else if (control instanceof BooleanSignal) {
      json = this.serializeBooleanSignal(control);
    } else if (control instanceof Button) {
      json = this.serializeButton(control);
    } else if (control instanceof EnumSignal) {
      json = this.serializeEnumSignal(control);
    } else if (control instanceof Gauge) {
      json = this.serializeGauge(control);
    } else if (control instanceof GridLayout) {
      json = this.serializeGridLayout(control);
    } else if (control instanceof GridLayoutRow) {
      json = this.serializeGridLayoutRow(control);
    } else if (control instanceof GridLayoutCell) {
      json = this.serializeGridLayoutCell(control);
    } else if (control instanceof Hint) {
      json = this.serializeHint(control);
    } else if (control instanceof Image) {
      json = this.serializeImage(control);
    } else if (control instanceof Label) {
      json = this.serializeLabel(control);
    } else if (control instanceof LineChart) {
      json = this.serializeLineChart(control);
    } else if (control instanceof NumericSignal) {
      json = this.serializeNumericSignal(control);
    } else if (control instanceof StackLayout) {
      json = this.serializeStackLayout(control);
    } else if (control instanceof Text) {
      json = this.serializeText(control);
    } else if (control instanceof Header) {
      json = this.serializeHeader(control);
    } else if (control instanceof StringSignal) {
      json = this.serializeStringSignal(control);
    } else {
      throw new Error(`Unknown control type: ${control.constructor.name}.`);
    }

    return {
      ...json,
      ...(control.width !== control.fallbackWidth ? { width: control.width } : {}),
      ...(control.height !== control.fallbackHeight ? { height: control.height } : {}),
      ...(control.margin.top !== '0du' ||
      control.margin.bottom !== '0du' ||
      control.margin.right !== '0du' ||
      control.margin.left !== '0du'
        ? {
            margin: {
              top: control.margin.top,
              bottom: control.margin.bottom,
              right: control.margin.right,
              left: control.margin.left,
            },
          }
        : {}),
    };
  }

  serializeSignalIdentifier(signal: SignalIdentifier): AnySignalIdentifierDeclaration {
    if (signal instanceof ArraySignalIdentifier) {
      return {
        type: 'ArraySignalIdentifier',
        signalType: signal.signalType,
        index: signal.index,
        source: this.serializeSignalSource(signal.source),
        ...(signal.unit === '' ? {} : { unit: signal.unit }),
        ...(signal.options.length === 0 ? {} : { options: signal.options }),
        ...(signal.scale.multiplier !== 1 || signal.scale.offset !== 0
          ? { scale: this.serializeSignalScale(signal.scale) }
          : {}),
        ...(signal.formatter.fractionDigits !== 0 || signal.formatter.fractionLimiting !== 'round'
          ? { format: this.serializeSignalFormat(signal.formatter) }
          : {}),
      };
    } else {
      return {
        type: 'SignalIdentifier',
        signalType: signal.signalType,
        source: this.serializeSignalSource(signal.source),
        ...(signal.unit === '' ? {} : { unit: signal.unit }),
        ...(signal.options.length === 0 ? {} : { options: signal.options }),
        ...(signal.scale.multiplier !== 1 || signal.scale.offset !== 0
          ? { scale: this.serializeSignalScale(signal.scale) }
          : {}),
        ...(signal.formatter.fractionDigits !== 0 || signal.formatter.fractionLimiting !== 'round'
          ? { format: this.serializeSignalFormat(signal.formatter) }
          : {}),
      };
    }
  }

  serializeSignalSource(source: SignalSource): AnySignalSourceDeclaration {
    if (source instanceof EcuSignalSource) {
      return { type: 'EcuSignalSource', signalName: source.signalName, ecuId: source.ecuId };
    } else if (source instanceof ClientDeviceSignalSource) {
      return { type: 'ClientDeviceSignalSource', signalName: source.signalName };
    }
    return null;
  }

  serializeSignalScale(scale: SignalScale): SignalScaleDeclaration {
    return { multiplier: scale.multiplier, offset: scale.offset };
  }

  serializeSignalFormat(format: SignalFormatter): SignalFormatDeclaration {
    return { fractionDigits: format.fractionDigits, fractionLimiting: format.fractionLimiting };
  }

  serializeSignalBinding(signal: SignalBinding): TextControlDeclaration.SignalBinding {
    return {
      signalID: this.serializeSignalIdentifier(signal.signalId),
      variableName: signal.variableName,
    };
  }

  serializeText(control: Text): TextControlDeclaration {
    return {
      id: control.id,
      type: 'Text',
      text: control.text,
      align: control.align,
      signals: control.signals.map((signal) => this.serializeSignalBinding(signal)),
    };
  }

  serializeLabel(label: Label): LabelControlDeclaration {
    return {
      id: label.id,
      type: 'Label',
      text: label.text,
      align: label.align,
    };
  }

  serializeHint(control: Hint): HintControlDeclaration {
    return {
      id: control.id,
      type: 'Hint',
      title: control.title,
      text: control.text,
      align: control.align,
    };
  }

  serializeNumericSignal(control: NumericSignal): NumericSignalControlDeclaration {
    return {
      id: control.id,
      type: 'NumericSignal',
      readonly: control.readonly,
      standalone: control.standalone,
      areas: control.areas.map((area) => this.serializeArea(area)),
      signalID: this.serializeSignalIdentifier(control.signalId),
    };
  }

  serializeLine(line: Line): LineChartControlDeclaration.Line {
    return {
      label: line.label,
      color: line.color,
      signalID: this.serializeSignalIdentifier(line.signalId),
    };
  }

  serializeLineChart(control: LineChart): LineChartControlDeclaration {
    return {
      id: control.id,
      type: 'LineChart',
      minimum: control.minimum,
      maximum: control.maximum,
      valuesCount: control.valuesCount,
      lines: control.lines.map((line) => this.serializeLine(line)),
    };
  }

  serializeEnumSignal(control: EnumSignal): EnumSignalControlDeclaration {
    return {
      id: control.id,
      type: 'EnumSignal',
      readonly: control.readonly,
      standalone: control.standalone,
      signalID: this.serializeSignalIdentifier(control.signalId),
    };
  }

  serializeBar(bar: Bar): BarChartControlDeclaration.Bar {
    return {
      label: bar.label,
      color: bar.color,
      signalID: this.serializeSignalIdentifier(bar.signalId),
    };
  }

  serializeBarChart(control: BarChart): BarChartControlDeclaration {
    return {
      id: control.id,
      type: 'BarChart',
      minimum: control.minimum,
      maximum: control.maximum,
      direction: control.direction,
      bars: control.bars.map((bar) => this.serializeBar(bar)),
    };
  }

  serializeBooleanSignal(control: BooleanSignal): BooleanSignalControlDeclaration {
    return {
      id: control.id,
      type: 'BooleanSignal',
      readonly: control.readonly,
      standalone: control.standalone,
      signalID: this.serializeSignalIdentifier(control.signalId),
    };
  }

  serializeApplication(app: Application): ApplicationDeclaration {
    const pages = app.rootPage.children.map((page) => this.serializePage(page));
    const themeOptions = {
      brandColor: { background: app.themeOptions.brandColor.background },
      primaryColor: { background: app.themeOptions.primaryColor.background },
      pageColor: { background: app.themeOptions.pageColor.background },
      headerPresets: app.themeOptions.headerPresets.map((preset) => {
        return { align: preset.align, fontSize: preset.fontSize, id: preset.id, label: preset.label };
      }),
    };
    const declaration: ApplicationDeclaration = {
      formatVersion,
      pages,
      themeOptions,
    };

    if (app.screenTypes.length > 0) {
      declaration.screenTypes = app.screenTypes.map((item) => this.serializeScreenType(item));
    }

    return declaration;
  }

  serializeScreenType(screenType: ScreenType): ScreenTypeDeclaration {
    return {
      id: screenType.id,
      name: screenType.name.raw,
      minWidth: screenType.minWidth.raw,
      orientation: screenType.orientation.raw,
    };
  }

  serializePage(page: Page): PageDeclaration {
    const declaration: PageDeclaration = {
      id: page.id,
      name: page.name,
    };

    if (page.controlRoot.children.length !== 0) {
      declaration.controls = page.controlRoot.children.map((child) => this.serializeControl(child));
    }

    if (page.children.length !== 0) {
      declaration.pages = page.children.map((item) => this.serializePage(item));
    }

    if (page.variations.length !== 0) {
      declaration.variations = page.variations.map((item) => this.serializePageVariation(item));
    }

    return declaration;
  }

  serializePageVariation(variation: PageVariation): PageVariationDeclaration {
    return {
      screenTypeId: variation.screenType.raw != null ? variation.screenType.raw.id : null,
      controlRoot: this.serializeRootControl(variation.controlRoot),
    };
  }

  serializeRootControl(control: Root): RootControlDeclaration {
    return {
      id: control.id,
      type: 'Root',
      children: control.children.map((child) => this.serializeControl(child)),
    };
  }

  serializeStackLayout(stackLayout: StackLayout): StackLayoutControlDeclaration {
    const children = stackLayout.children.map((child) => this.serializeControl(child));

    return {
      id: stackLayout.id,
      type: 'StackLayout',
      direction: stackLayout.direction,
      children,
    };
  }

  serializeGridLayout(control: GridLayout): GridLayoutControlDeclaration {
    return {
      id: control.id,
      type: 'GridLayout',
      columns: control.columns,
      spacing: control.spacing,
      children: control.children.map((item) => this.serializeControl(item) as GridLayoutRowControlDeclaration),
    };
  }

  serializeGridLayoutRow(control: GridLayoutRow): GridLayoutRowControlDeclaration {
    return {
      id: control.id,
      type: 'GridLayoutRow',
      children: control.children.map((item) => this.serializeControl(item) as GridLayoutCellControlDeclaration),
    };
  }

  serializeGridLayoutCell(control: GridLayoutCell): GridLayoutCellControlDeclaration {
    return {
      id: control.id,
      type: 'GridLayoutCell',
      children: control.children.map((item) => this.serializeControl(item)),
    };
  }

  serializeImage(control: Image): ImageControlDeclaration {
    return {
      id: control.id,
      type: 'Image',
      resource: control.resource,
      intrinsicAspectRatio: control.intrinsicAspectRatio,
    };
  }

  serializeArea(area: Area): Area {
    const declaration: Area = { range: area.range, color: area.color };

    if (area.warning != null) {
      declaration.warning = area.warning;
    }

    return declaration;
  }

  serializeGauge(control: Gauge): GaugeControlDeclaration {
    return {
      id: control.id,
      type: 'Gauge',
      minimum: control.minimum,
      maximum: control.maximum,
      areas: control.areas.map((area) => this.serializeArea(area)),
      signalID: this.serializeSignalIdentifier(control.signalId),
      valueForegroundColor: control.valueForegroundColor,
      useThemeColor: control.useThemeColor,
    };
  }

  serializeButton(control: Button): ButtonControlDeclaration {
    return {
      id: control.id,
      type: 'Button',
      text: control.text,
      action: this.serializeButtonAction(control.action),
    };
  }

  serializeButtonAction(action: ButtonAction): AnyButtonActionDeclaration {
    if (action instanceof WriteSignalsButtonAction) {
      return {
        type: 'writeSignals',
      };
    } else if (action instanceof NavigatePageButtonAction) {
      return {
        type: 'navigatePage',
        pageId: action.pageId,
      };
    } else if (action instanceof SetPulseButtonAction) {
      return {
        type: 'setPulse',
        signalId: this.serializeSignalIdentifier(action.signalId),
      };
    } else {
      throw new Error(`Unexpected button action: ${action}.`);
    }
  }

  serializeHeader(control: Header): HeaderControlDeclaration {
    return {
      id: control.id,
      type: 'Header',
      text: control.text,
      preset: control.preset,
    };
  }

  serializeStringSignal(control: StringSignal): StringSignalControlDeclaration {
    return {
      id: control.id,
      type: 'StringSignal',
      readonly: control.readonly,
      standalone: control.standalone,
      signalID: this.serializeSignalIdentifier(control.signalId),
    };
  }
}
